import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import axiosInstance from '../axiosConfig';
import { AuthContext } from '../AuthContext';

const isIgnoredRequestError = (error) => (
    error?.code === 'ERR_CANCELED'
    || error?.code === 'ECONNABORTED'
);

function AddToUpgradeModal({ show, handleClose, componentId, onUpdateUpgrade }) {
    const { selectedServiceCenter } = useContext(AuthContext);
    const [upgrades, setUpgrades] = useState([]);
    const [selectedUpgrade, setSelectedUpgrade] = useState('');
    const [quantityInUpgrade, setQuantityInUpgrade] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!componentId || !selectedServiceCenter || !show) {
            return undefined;
        }

        const controller = new AbortController();
        setError(null);

        axiosInstance.get(`/api/upgrades_for_component/${componentId}/`, {
            params: { service_center: selectedServiceCenter.id },
            signal: controller.signal,
        })
        .then(response => {
            if (controller.signal.aborted) {
                return;
            }
            setUpgrades(response.data.upgrades || []);
        })
        .catch(error => {
            if (isIgnoredRequestError(error)) {
                return;
            }
            console.error('Error fetching upgrades:', error);
            setError('Не удалось загрузить апгрейды. Попробуйте позже.');
        });

        return () => controller.abort();
    }, [componentId, selectedServiceCenter, show]);

    const handleSave = () => {
        if (selectedUpgrade && quantityInUpgrade <= 0) {
            setError('Пожалуйста, введите количество для выбранного апгрейда.');
            return;
        }

        const formData = new FormData();
        formData.append('component_id', componentId);
        formData.append('upgrade_id', selectedUpgrade || ''); // Позволяем пустое значение для отвязки
        formData.append('quantity_in_upgrade', quantityInUpgrade);
        formData.append('service_center', selectedServiceCenter.id);

        axiosInstance.post('/api/component/add_to_upgrade/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then(response => {
            if (response.data.success) {
                handleClose();
                onUpdateUpgrade(); // Обновляем данные апгрейда после успешного добавления компонента
                if (selectedUpgrade) {
                    window.Toastify({
                        text: "Компонент успешно добавлен к апгрейду!",
                        duration: 3000,
                        gravity: "top",
                        position: "right",
                        backgroundColor: "linear-gradient(to right, #00b09b, #96c93d)",
                        stopOnFocus: true,
                    }).showToast();
                } else {
                    window.Toastify({
                        text: "Апгрейд успешно отвязан!",
                        duration: 3000,
                        gravity: "top",
                        position: "right",
                        backgroundColor: "linear-gradient(to right, #ff5f6d, #ffc371)",
                        stopOnFocus: true,
                    }).showToast();
                }
            } else {
                setError('Произошла ошибка при добавлении к апгрейду.');
            }
        })
        .catch(error => {
            console.error('Request failed:', error);
            setError('Произошла ошибка при выполнении операции. Пожалуйста, попробуйте еще раз.');
        });
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Добавить к апгрейду</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group controlId="upgrade-select">
                        <Form.Label>Выберите апгрейд</Form.Label>
                        <Form.Control
                            as="select"
                            value={selectedUpgrade}
                            onChange={(e) => setSelectedUpgrade(e.target.value)}
                        >
                            <option value="">Отвязать апгрейд</option>
                            {upgrades.map((upgrade) => (
                                <option key={upgrade.id} value={upgrade.id}>
                                    {upgrade.name}
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    {selectedUpgrade && (
                        <Form.Group controlId="quantity_in_upgrade">
                            <Form.Label>Количество в апгрейде</Form.Label>
                            <Form.Control
                                type="number"
                                value={quantityInUpgrade}
                                onChange={(e) => setQuantityInUpgrade(e.target.value)}
                                required
                            />
                        </Form.Group>
                    )}
                    {error && <div className="alert alert-danger">{error}</div>}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Отмена
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default AddToUpgradeModal;
